import path from 'path'

import type { InitOptions } from '../../operations/init'
import { init } from '../../operations/init'
import { cliCommand } from '../../utils/invocation'
import { fail } from '../fail'
import { offerDeploy } from '../offer-deploy'
import { offerGlobalInstall } from '../offer-global-install'
import { say, section } from '../output'
import { createPrompter } from '../prompts/ask'
import { confirm } from '../prompts/confirm'
import { reportCreateService, reportInit } from '../reporter'
import type { CreateServiceCommandOptions } from './create-service'
import { answersFrom } from './create-service'

export interface InitCommandOptions extends CreateServiceCommandOptions {
  /** Where the project goes. Defaults to the current directory. */
  dir?: string
  /** Written into `flowrunner.json`; defaults to the CLI's own default server. */
  server?: string
  /** `--no-install` sets this to false. */
  install?: boolean
  /** `--no-git` sets this to false. */
  git?: boolean
  /** `--no-tests` sets this to false. */
  tests?: boolean
}

export async function initCommand(
  templateId: string | undefined,
  options: InitCommandOptions = {}
): Promise<void> {
  const prompter = process.stdin.isTTY ? createPrompter() : null

  try {
    const initOptions: InitOptions = {
      cwd           : options.dir ?? '.',
      template      : templateId,
      server        : options.server,
      install       : options.install,
      git           : options.git,
      tests         : options.tests,
      answers       : answersFrom(options),
      acceptDefaults: options.yes,
      ask           : prompter?.ask,
      onEvent       : reportInit,
      onServiceEvent: reportCreateService,
    }

    if (process.stdin.isTTY) {
      initOptions.onConfirmTarget = target => confirm('Create the project here?', target.defaultYes)
    }

    const result = await init(initOptions)

    prompter?.close()

    if (result.cancelled) {
      return
    }

    const relative = path.relative(process.cwd(), result.projectRoot)
    // A relative path that climbs out of the current directory is harder to read than the absolute one.
    const where = !relative || relative.startsWith('..') ? result.projectRoot : relative
    const step = (index: number, text: string) => say(`  ${ index }. ${ text }`)

    // Before anything that prints a command to run: accepting this changes those hints from `npx
    // flowrunner …` to the bare form.
    await offerGlobalInstall()

    // Deploying from here only makes sense with a service to deploy, and only from the project directory —
    // `--dir` elsewhere would deploy whatever happens to be under the current one.
    if (result.service && result.projectRoot === process.cwd() && await offerDeploy(result.service.serviceId)) {
      return
    }

    section('Next steps:')

    let index = 1

    if (result.projectRoot !== process.cwd()) {
      step(index++, `cd ${ where }`)
    }

    if (!result.installed) {
      step(index++, 'npm install')
    }

    if (result.service) {
      step(index++, `edit services/${ result.service.serviceId }/src/index.js`)
    } else {
      step(index++, `${ cliCommand() } cs   — scaffold your first service`)
    }

    // Read off the flag rather than off what was written: a re-run in a project that already has the
    // harness writes nothing, and `npm test` is still the right next step there.
    if (options.tests !== false) {
      step(index++, 'npm test   — the unit suites in services/*/tests/')
    }

    step(index++, `${ cliCommand() } deploy   — logs you in first if you have not already`)

    // `init` writes no agent files of its own — which file an agent reads is provider-specific — so this
    // line is the only thing telling a Claude Code user the wiring exists.
    step(index, `${ cliCommand() } init-claude   — optional, wires the FlowRunner agents into Claude Code`)

    say('')
  } catch (error) {
    fail(error)
  } finally {
    prompter?.close()
  }
}
